import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown, Star, ShieldCheck, ThumbsUp, CheckCircle } from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "@/lib/utils";
import { QuoteModal } from "./QuoteModal";

const MobileNavItem = ({ link, setIsOpen }: { link: any, setIsOpen: (v: boolean) => void }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!link.dropdown) {
    return (
      <a
        href={link.path}
        className="text-lg font-medium text-foreground/80 hover:text-primary py-2"
        onClick={() => setIsOpen(false)}
      >
        {link.name}
      </a>
    );
  }

  return (
    <div className="flex flex-col">
      <button
        className="flex items-center justify-between text-lg font-medium text-foreground/80 hover:text-primary py-2 w-full text-left"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {link.name}
        <ChevronDown size={18} className={cn("transition-transform duration-200", isExpanded ? "rotate-180" : "")} />
      </button>
      
      {isExpanded && (
        <div className="flex flex-col pl-4 border-l-2 border-primary/20 mt-1 space-y-2">
          {link.dropdown.map((dropLink: any) => (
            <Link
              key={dropLink.name}
              to={dropLink.path}
              className="text-base text-foreground/60 hover:text-primary py-1"
              onClick={() => setIsOpen(false)}
            >
              {dropLink.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Start", path: "/" },
    { 
      name: "Leistungen", 
      path: "/#leistungen",
      dropdown: [
        { name: "Umzugsservice", path: "/umzugsservice" },
        { name: "Möbelmontage", path: "/moebelmontage" },
        { name: "Bodenbelag (Laminat, Vinyl)", path: "/bodenbelag" },
        { name: "Lichtinstallation", path: "/lichtinstallation" },
        { name: "Pflasterarbeiten", path: "/pflasterarbeiten" },
      ]
    },
    { name: "Über uns", path: "/ueber-uns" },
    { 
      name: "Servicegebiete", 
      path: "/#servicegebiete",
      dropdown: [
        { name: "Augsburg", path: "/augsburg" },
        { name: "Ulm", path: "/ulm" },
        { name: "Günzburg", path: "/guenzburg" },
        { name: "Landsberg", path: "/landsberg" },
        { name: "München", path: "/muenchen" },
        { name: "Ingolstadt", path: "/ingolstadt" },
        { name: "Donauwörth", path: "/donauwoerth" },
        { name: "Neuburg an der Donau", path: "/neuburg-an-der-donau" },
        { name: "Heidenheim an der Brenz", path: "/heidenheim-an-der-brenz" },
      ]
    },
    { name: "Kontakt", path: "/kontakt" },
  ];

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background border-b",
        isScrolled ? "shadow-md" : ""
      )}
    >
      <div className="bg-neutral-900 text-white text-xs py-2 hidden md:block">
        <div className="container mx-auto px-4 md:px-6 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-1.5">
              <ThumbsUp size={14} className="text-primary" />
              <span className="font-medium">100% Kundenzufriedenheit</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle size={14} className="text-primary" />
              <span className="font-medium">Festpreisgarantie</span>
            </div>
            <div className="flex items-center gap-1.5">
              <ShieldCheck size={14} className="text-primary" />
              <span className="font-medium">Versicherter Umzug</span>
            </div>
          </div>
          <a href="https://share.google/xw8JE3IKl3tC9Kcw0" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-primary transition-colors">
            <div className="flex gap-0.5 text-[#fabb05]">
              <Star size={12} fill="currentColor" />
              <Star size={12} fill="currentColor" />
              <Star size={12} fill="currentColor" />
              <Star size={12} fill="currentColor" />
              <Star size={12} fill="currentColor" />
            </div>
            <span className="font-bold">5.0</span>
            <span className="font-medium">Google Bewertungen</span>
          </a>
        </div>
      </div>
      <div className={cn("container mx-auto px-4 md:px-6 transition-all duration-300", isScrolled ? "py-3" : "py-5")}>
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_300/u_https://assets.cdn.filesafe.space/Zrqvy6cNzSbnzDV55cv1/media/6936d9b74b202f068ab37a46.png" 
              alt="Easy Umzug Logo" 
              width="143"
              height="40"
              className="h-10 w-auto"
              fetchPriority="high"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <div className="flex items-center gap-6">
              {navLinks.map((link) => (
                <div key={link.name} className="relative group">
                  <a
                    href={link.path}
                    className="flex items-center gap-1 text-sm font-medium text-foreground/80 hover:text-primary transition-colors py-2"
                  >
                    {link.name}
                    {link.dropdown && <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-200" />}
                  </a>
                  
                  {link.dropdown && (
                    <div className="absolute top-full left-0 w-64 bg-background border shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 translate-y-2 group-hover:translate-y-0 flex flex-col py-2 z-50">
                      {link.dropdown.map((dropLink) => (
                        <Link
                          key={dropLink.name}
                          to={dropLink.path}
                          className="px-4 py-2 text-sm text-foreground/80 hover:text-primary hover:bg-neutral-50 transition-colors"
                        >
                          {dropLink.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="flex items-center gap-6 ml-4">
              <a 
                href="tel:+4915888640762" 
                className="text-sm font-bold text-foreground hover:text-primary transition-colors"
              >
                +49 15888 640762
              </a>
              <QuoteModal>
                <Button className="px-6">
                  Angebot anfordern
                </Button>
              </QuoteModal>
            </div>
          </nav>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden text-foreground"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-background border-b shadow-lg py-4 px-4 flex flex-col gap-4 max-h-[calc(100vh-80px)] overflow-y-auto">
          {navLinks.map((link) => (
            <MobileNavItem key={link.name} link={link} setIsOpen={setIsOpen} />
          ))}
          <div className="pt-4 mt-2 border-t flex flex-col gap-4">
            <a href="https://share.google/xw8JE3IKl3tC9Kcw0" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-foreground hover:text-primary transition-colors">
              <div className="flex gap-0.5 text-[#fabb05]">
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
              <span className="font-bold text-lg">5.0</span>
              <span className="font-medium">Google Bewertungen</span>
            </a>
            <a 
              href="tel:+4915888640762" 
              className="text-lg font-bold text-foreground"
            >
              +49 15888 640762
            </a>
            <QuoteModal>
              <Button className="w-full" onClick={() => setIsOpen(false)}>
                Angebot anfordern
              </Button>
            </QuoteModal>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;